import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table'
import { EmptyState } from './states'

interface DataTableProps<T> {
	columns: ColumnDef<T, any>[]
	rows: T[]
	emptyMessage: string
	onRowClick?: (row: T) => void
	// Highlights rows that are currently acting as a filter elsewhere on the page.
	isRowSelected?: (row: T) => boolean
}

// Sorting and filtering are done by the backend, not here: the lists are
// paginated server-side, so sorting only the current page would silently lie
// about the ordering of the whole set.
export function DataTable<T>({ columns, rows, emptyMessage, onRowClick, isRowSelected }: DataTableProps<T>) {
	const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() })

	if (rows.length === 0) return <EmptyState message={emptyMessage} />

	return (
		// Wide tables scroll inside their own container so the page body never
		// scrolls sideways.
		<div className="overflow-x-auto">
			<table className="w-full border-collapse text-sm">
				<thead>
					{table.getHeaderGroups().map((group) => (
						<tr key={group.id} className="border-b border-line">
							{group.headers.map((header) => (
								<th
									key={header.id}
									className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft"
								>
									{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody>
					{table.getRowModel().rows.map((row) => (
						<tr
							key={row.id}
							onClick={onRowClick ? () => onRowClick(row.original) : undefined}
							aria-selected={isRowSelected ? isRowSelected(row.original) : undefined}
							className={`border-b border-line last:border-0 ${
								onRowClick ? 'cursor-pointer hover:bg-canvas' : ''
							} ${isRowSelected?.(row.original) ? 'bg-canvas font-medium shadow-[inset_3px_0_0_var(--color-brand)]' : ''}`}
						>
							{row.getVisibleCells().map((cell) => (
								<td key={cell.id} className="px-4 py-3 align-middle">
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}
